
import React, { useState } from 'react';
import { motion, Variants, RepeatType, AnimatePresence, useScroll, useTransform } from 'framer-motion';
import { Sparkles, Cpu, BookOpen, Code, GraduationCap, Compass, AlertCircle, CheckCircle2, UserCheck, ArrowRight, X, Loader2 } from 'lucide-react';
import PageTransition from '@/components/global/PageTransition';
import SEO from '@/components/global/SEO';
import { Language } from '@/types';
import NovaNavbar from '@/components/layout/NovaNavbar';
import { useNavigate } from 'react-router-dom';
import NovaHeroLogoStage from '@/components/marketing/NovaHeroLogoStage';
import { sanitizeCss } from '@/lib/security/sanitize';

interface NovaPageProps {
  lang: Language;
}

const marqueeVariants: Variants = {
  animate: (direction: 'left' | 'right') => ({
    x: direction === 'left' ? ['0%', '-50%'] : ['-50%', '0%'],
    transition: {
      x: {
        repeat: Infinity,
        repeatType: "loop" as RepeatType,
        duration: 60,
        ease: "linear",
      },
    },
  }),
};

/** Reusable Premium Image Frame for Visual Proof (Ready for real images) */
const VisualProofImage: React.FC<{ src?: string, alt: string }> = ({ src, alt }) => {
  return (
    <div className="w-full bg-[#050608] border border-white/10 rounded-2xl overflow-hidden shadow-2xl relative group">
      {/* Background Glow */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#7F77DD]/10 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-1000" />

      {src ? (
        <img src={src} alt={alt} className="w-full h-auto transition-transform duration-700 group-hover:scale-[1.01]" />
      ) : (
        <div className="w-full h-full flex items-center justify-center p-8">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
              <Sparkles className="text-[#7F77DD]/50" size={28} />
            </div>
            <p className="text-slate-600 font-bold uppercase text-[10px] tracking-[0.4em]">{alt}</p>
            <p className="text-slate-700 text-[9px] tracking-widest">[ Mockup Placeholder ]</p>
          </div>
        </div>
      )}

      {/* Glossy Overlay effect */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-tr from-white/[0.02] to-transparent opacity-50" />
    </div>
  );
};

const NovaPage: React.FC<NovaPageProps> = ({ lang }) => {
  const isRtl = lang === 'ar';
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [isPreparing, setIsPreparing] = useState(false);

  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 400], [0, 1]);

    const t = {
    en: {
      badge: "AUTONOMOUS SPATIAL AI PLATFORM",
      title: "Your End-to-End Autonomous\nSpatial AI Platform.",
      subtitle: "Generate production ArcPy & PostGIS pipelines, automate satellite image processing, heal broken GIS topology, and run sub-millisecond in-memory spatial analytics.",
      cta: "Launch Workspace →",
      ctaSecondary: "Explore Platform",
      trust: "Trusted by spatial engineers & research labs",
      placeholder: "Ask Nova to build a spatial pipeline, heal topology, or query data...",
      preparing: "Initializing your spatial workspace...",
      useCases: {
        title: "Proven Spatial Impact Across the Field",
        items: [
          {
            title: "Master any GIS & Remote Sensing concept — instantly",
            sub: "VEXL Nova breaks down complex geodetic, projection, and spectral topics with practical mathematical clarity.",
            alt: "Understand GIS Concepts with VEXL Nova",
            src: "/products/nova/11.webp",
            id: "lectures"
          },
          {
            title: "Automate geoprocessing with verified, production-grade code",
            sub: "ArcPy, PyQGIS, Earth Engine, and GeoPandas pipelines — fully validated, syntax-checked, and explained.",
            alt: "Write GIS Assignments with Real Code",
            src: "/products/nova/22.webp",
            id: "assignments"
          },
          {
            title: "Accelerate academic research & graduation projects",
            sub: "From formal spatial methodology to spectral band indices and statistical validation — Nova guides every milestone.",
            alt: "Graduation Project Guidance",
            src: "/products/nova/33.webp",
            id: "project"
          },
          {
            title: "Eliminate hours of manual workflow searching",
            sub: "Get the exact spatial tool, geodetic conversion, or topology-healing operator immediately.",
            alt: "Instant GIS Tool Recommendations",
            src: "/products/nova/44.webp",
            id: "tools"
          },
        ]
      },
      struggle: {
        title: "Why geospatial workflows are demanding",
        subtitle: "The real obstacles facing engineers, researchers, and students",
        items: [
          { label: "Confusing CRS & Datum mismatches", icon: <X className="text-red-500" /> },
          { label: "Outdated ArcPy & GIS documentation", icon: <X className="text-red-500" /> },
          { label: "Complex multi-spectral band mathematics", icon: <X className="text-red-500" /> },
          { label: "Exhausting manual topology debugging", icon: <X className="text-red-500" /> }
        ],
        solution: "VEXL Nova eliminates the friction."
      },
      demo: {
        title: "See VEXL Nova in action",
        userQuery: "Write NDVI code using Sentinel-2",
        response: {
          code: `import ee

def calculate_ndvi(image):
    return image.normalizedDifference(['B8', 'B4'])`,
          explanation: "This function uses the Near-Infrared (B8) and Red (B4) bands of Sentinel-2 to calculate vegetation health."
        }
      },
      finalCTA: "Join engineers, researchers, and students accelerating spatial intelligence with VEXL Nova.",
      prompts: [
        "How to use Buffer in ArcGIS Pro?", "Explain NDVI in simple terms", "Write an ArcPy script for Clip",
        "How to calculate slope from DEM?", "Compare QGIS and ArcGIS Pro", "Download Sentinel-2 data",
        "Fix ERROR 000210 in ArcPy", "How to use Join Field?", "Convert Shapefile to GeoJSON",
        "Map vegetation health in Delta", "Analyze urban growth in Cairo", "Calculate distance between points",
        "How to use Kriging interpolation?", "Fix coordinate system issues", "Automate map export to PDF",
        "Calculate area of polygons", "Find nearest hospital to school", "Explain Coordinate Systems",
        "How to use Raster Calculator?", "Plan a new road network", "Analyze traffic patterns",
        "Detect land use changes", "Map flood risk areas", "Analyze soil types",
        "Calculate water body index", "Optimize irrigation paths", "Detect building footprints",
        "Analyze city population density", "Generate random sample points", "Find best location for solar panels",
        "Map earthquake zones", "Monitor forest fires", "Analyze satellite image bands",
        "Remove clouds from GEE images", "Calculate building heights", "Predict farm crop yields"
      ],
      faq: {
        title: "Frequently Asked Questions",
        subtitle: "Key answers on VEXL Nova's spatial reasoning capabilities.",
        items: [
          {
            q: "What is VEXL Nova?",
            a: "VEXL Nova is a specialized Spatial Reasoning Engine and Interactive Workspace developed by VEXL. It connects GIS, Remote Sensing, and Surveying to specialized AI, enabling engineers, researchers, and students to generate verified spatial code, analyze satellite data, and automate geospatial intelligence."
          },
          {
            q: "Who is VEXL Nova designed for?",
            a: "Nova is built for the entire geospatial continuum: Professional Engineers (automating ArcPy, PyQGIS, and PostGIS pipelines), Academic Researchers (M.Sc./Ph.D. methodology and spectral calculations), and Students (mastering GIS foundations and completing academic projects)."
          },
          {
            q: "Does Nova support Surveying and Geodetic workflows?",
            a: "Yes. Nova is expanding into surveying, GNSS coordinate adjustments, cadastral transformations, and geodetic datum corrections alongside its core GIS and Earth Observation capabilities."
          }
        ]
      }
    },
    ar: {
      badge: "منصة الذكاء الاصطناعي المكاني المستقلة الشاملة",
      title: "منصتك المكانية الشاملة\nوالمستقلة للذكاء الاصطناعي الجغرافي.",
      subtitle: "توليد خطوط إنتاج ArcPy و PostGIS، أتمتة معالجة الصور الفضائية، إصلاح طوبولوجيا الـ GIS المتضررة، وتشغيل تحليلات مكانية في الذاكرة بأجزاء من الملي ثانية.",
      cta: "ابدأ مساحة العمل ←",
      ctaSecondary: "استكشف المنصة",
      trust: "موثوق من مهندسي البيانات المكانية ومختبرات الأبحاث",
      placeholder: "اطلب من نوفا بناء خط معالجة مكاني، إصلاح الطوبولوجيا، أو تحليل البيانات...",
      preparing: "جاري تجهيز بيئة العمل المكانية...",
      useCases: {
        title: "إثبات عملي: نتائج ميدانية موثقة",
        items: [
          {
            title: "استيعاب أي مفهوم مكاني في الـ GIS والاستشعار فوراً",
            sub: "تشرح VEXL Nova المفاهيم الجيوديسية والطيفية المعقدة بوضوح رياضي وعلمي دقيق وأمثلة واقعية.",
            alt: "فهم مفاهيم الـ GIS والاستشعار مع نوفا",
            src: "/products/nova/11.webp",
            id: "lectures"
          },
          {
            title: "أتمتة العمليات المكانية بأكواد برمجية موثوقة ومفحوصة",
            sub: "توليد سكربتات ArcPy و PyQGIS و Earth Engine و GeoPandas — مدققة برمجياً وسليمة الإسقاطات.",
            alt: "أكواد برمجية مكانية حقيقية",
            src: "/products/nova/22.webp",
            id: "assignments"
          },
          {
            title: "تسريع الأبحاث العلمية ومشاريع التخرج باحترافية",
            sub: "من صياغة المنهجيات المكانية المعتمدة إلى حساب الباندات الفضائية والتحقق الإحصائي خطوة بخطوة.",
            alt: "إرشاد كامل للأبحاث ومشاريع التخرج",
            src: "/products/nova/33.webp",
            id: "project"
          },
          {
            title: "إنهاء ساعات البحث اليدوي عن أدوات التحليل",
            sub: "الحصول على الأداة الجغرافية وسير العمل الدقيق أو معالج التبولوجيا فوراً دون إهدار وقت.",
            alt: "اقتراح الأدوات المكانية الدقيقة",
            src: "/products/nova/44.webp",
            id: "tools"
          },
        ]
      },
      struggle: {
        title: "لماذا يعد العمل الجغرافي والمكاني معقداً؟",
        subtitle: "العقبات اليومية التي تواجه المهندسين والباحثين والطلاب",
        items: [
          { label: "تضارب أنظمة الإسقاط والمراجع الجيوديسية (CRS/Datums)", icon: <X className="text-red-500" /> },
          { label: "التوثيق البرمجي القديم لأكواد ArcPy ومكتبات GIS", icon: <X className="text-red-500" /> },
          { label: "تعقيدات حساب المؤشرات الطيفية للأقمار الصناعية", icon: <X className="text-red-500" /> },
          { label: "استنزاف الوقت في إصلاح أخطاء التبولوجيا والبيانات", icon: <X className="text-red-500" /> }
        ],
        solution: "VEXL Nova تقضي على هذه التعقيدات بالكامل."
      },
      demo: {
        title: "شاهد VEXL Nova وهي تعمل",
        userQuery: "اكتب كود NDVI باستخدام Sentinel-2",
        response: {
          code: `import ee

def calculate_ndvi(image):
    return image.normalizedDifference(['B8', 'B4'])`,
          explanation: "تستخدم هذه الدالة نطاقات الأشعة تحت الحمراء القريبة (B8) والأحمر (B4) لقمر Sentinel-2 لحساب صحة الغطاء النباتي."
        }
      },
      finalCTA: "انضم إلى المهندسين والباحثين والطلاب الذين يطورون أعمالهم المكانية مع VEXL Nova.",
      prompts: [
        "كشف التوسع العمراني في القاهرة الجديدة", "تلخيص الوثائق التقنية المعقدة", "إنشاء سير عمل للتحليل المكاني",
        "تحليل مخاطر الزراعة في دلتا النيل", "كتابة مكون Rust للذكاء الاصطناعي الجغرافي", "تحديد التعدين غير القانوني من القمر الصناعي",
        "مقارنة أنماط نمو المدن", "التنبؤ بمناطق الفيضانات في السودان", "مراقبة عزل الكربون في الغابات",
        "رسم خرائط طاقة الرياح", "تنسيق تسليم أسراب الدرونز", "تحليل حركة المرور في قناة السويس",
        "محاكاة الجزر الحرارية الحضرية", "تقييم تدهور الأراضي في الساحل", "تحسين الشبكات الصغيرة للطاقة المتجددة",
        "تصنيف أنواع المحاصيل من الرادار", "مراقبة نضوب المياه الجوفية", "كشف المواقع الأثرية بواسطة LiDAR",
        "تقييم كفاءة مزارع الطاقة الشمسية", "تخطيط مسارات ترانزيت مستدامة", "التنبؤ بالازدحام المروري الحضري",
        "مراقبة انتشار الحرائق فورياً", "تقييم البنية التحتية بعد الكوارث", "رسم خرائط التنوع البيولوجي",
        "محاكاة تأثيرات ارتفاع منسوب البحر", "تحسين الري الدقيق للأراضي", "كشف تسرب الغاز من صور الدخان",
        "تحليل التحولات الديموغرافية في برلين", "إنشاء بيانات تدريب اصطناعية", "تحسين اللوجستيات للمدن الذكية",
        "رسم خرائط النشاط الزلزالي في المحيط الأطلسي", "مراقبة تراجع الأنهار الجليدية في أيسلندا", "تحليل نمط الحياة من SIGINT",
        "كشف صور الأقمار الصناعية المزيفة", "تنسيق أساطيل الجرارات ذاتية القيادة", "التنبؤ بإنتاجية مزارع القمح"
      ],
      faq: {
        title: "الأسئلة الشائعة",
        subtitle: "إجابات واضحة حول قدرات الاستدلال المكاني لمنصة فكسل نوفا.",
        items: [
          {
            q: "ما هو نظام فكسل نوفا (VEXL Nova)؟",
            a: "فكسل نوفا هو محرك استدلال وذكاء مكاني متخصص وبيئة عمل تفاعلية طورتها شركة VEXL. يربط النظام بين نظم المعلومات الجغرافية (GIS)، والاستشعار عن بعد، وهندسة المساحة بالذكاء الاصطناعي المتخصص لتمكين المهندسين والباحثين والطلاب من إنتاج أكواد برمجية دقيقة وأتمتة التحليلات الفضائية."
          },
          {
            q: "لمن تم تصميم فكسل نوفا؟",
            a: "تم تصميم نوفا ليخدم المنظومة المكانية بأكملها: المهندسون والمحترفون (أتمتة تحليلات ArcPy و PostGIS وإدارة البيانات)، الباحثون وأصحاب الدراسات العليا (صياغة المنهجيات ومعالجة مؤشرات الأقمار الصناعية)، والطلاب (تعلم المفاهيم وإنجاز مشاريع تخرج احترافية)."
          },
          {
            q: "هل يدعم نوفا أعمال المساحة والجيوديسيا؟",
            a: "نعم. يتوسع نوفا حالياً ليشمل حسابات المساحة، ومعالجة إحداثيات GNSS والرفع المساحي، والتحويلات الإحداثية والمراجع الجيوديسية إلى جانب قدراته الرائدة في نظم المعلومات الجغرافية والاستشعار عن بعد."
          }
        ]
      }
    }
  }[lang];

  const handleStartNow = () => {
    setIsPreparing(true);
    setTimeout(() => {
      const url = message.trim()
        ? `/nova-workspace?q=${encodeURIComponent(message.trim())}`
        : '/nova-workspace';
      navigate(url);
    }, 1800);
  };

  const pList = t.prompts;
  const chunk = Math.ceil(pList.length / 3);
  const row1 = [...pList.slice(0, chunk), ...pList.slice(0, chunk)];
  const row2 = [...pList.slice(chunk, chunk * 2), ...pList.slice(chunk, chunk * 2)];
  const row3 = [...pList.slice(chunk * 2), ...pList.slice(chunk * 2)];

  return (
    <>

      <PageTransition>
        <SEO
          title={lang === 'ar' ? 'VEXL Nova - محرك الاستدلال المكاني للجيوماتكس و GIS | Spatial AI Reasoning Engine' : 'VEXL Nova - Spatial AI Reasoning Engine | Geomatics & GIS'}
          description={lang === 'ar' ? "VEXL Nova هو محرك استدلال مكاني متقدم لمهندسي وباحثي نظم المعلومات الجغرافية والجيوماتكس والاستشعار عن بعد ونمذجة كوكب الأرض." : "VEXL Nova is an advanced Spatial AI Reasoning Engine for geomatics, GIS, and remote sensing engineers, researchers, and enterprises."}
          lang={lang}
          breadcrumbs={[
            { name: "Home", item: "/" },
            { name: "Nova", item: "/products/nova" }
          ]}
          schema={[
            {
              "@context": "https://schema.org",
              "@type": "SoftwareApplication",
              "@id": "https://vexl-gis.tech/products/nova#application",
              "name": "VEXL Nova",
              "applicationCategory": "GeospatialSoftware, DeveloperApplication, ResearchApplication",
              "operatingSystem": "Web, Windows, macOS, Linux",
              "description": lang === 'ar'
                ? "فكسل نوفا هو محرك استدلال مكاني متقدم وطبقة استخبارات جغرافية للمهندسين والباحثين، تم تطويره بواسطة VEXL لبناء أنظمة ذكاء اصطناعي موثوقة لنظم المعلومات الجغرافية والاستشعار عن بعد."
                : "VEXL Nova is an advanced Spatial AI Reasoning Engine developed by VEXL. It automates GIS modeling, ArcPy script generation, and academic research pipelines with deterministic geodetic constraints.",
              "publisher": {
                "@type": "Organization",
                "@id": "https://vexl-gis.tech/#organization",
                "name": "VEXL Studio",
                "url": "https://vexl-gis.tech",
                "logo": "https://vexl-gis.tech/logo.png",
                "founder": {
                  "@type": "Person",
                  "name": "Diaa Shousha",
                  "jobTitle": "Principal GeoAI Architect"
                }
              },
              "offers": {
                "@type": "Offer",
                "url": "https://vexl-gis.tech/products/nova",
                "priceCurrency": "USD",
                "price": "0",
                "availability": "https://schema.org/InStock",
                "description": "Community Free tier available with academic and professional licensing."
              }
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": t.faq.items.map((item: any) => ({
                "@type": "Question",
                "name": item.q,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": item.a
                }
              }))
            }
          ]}
        />

        <div className="bg-[#02040a] min-h-screen text-white overflow-x-hidden selection:bg-[#7F77DD]/35 selection:text-white">

          {/* Transition Overlay */}
          <AnimatePresence>
            {isPreparing && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-[100] bg-black flex flex-col items-center justify-center p-6"
              >
                <div className="absolute inset-0 bg-gradient-to-b from-[#7F77DD]/14 to-transparent pointer-events-none" />
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  className="mb-8"
                >
                  <Loader2 size={48} className="text-[#7F77DD]" />
                </motion.div>
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xl font-bold tracking-widest text-[#b7b0ff] uppercase"
                >
                  {t.preparing}
                </motion.h2>
              </motion.div>
            )}
          </AnimatePresence>

          <section id="hero" className="flex flex-col pt-0 relative overflow-hidden bg-[#02040a]">
            {/* Vibrant Identity Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#26215C]/40 via-[#7F77DD]/10 to-[#02040a] pointer-events-none" />

            {/* Transition Overlay (Blends Hero into the rest of the page) */}
            <motion.div
              style={{ opacity: heroOpacity }}
              className="absolute inset-0 bg-[#02040a] pointer-events-none z-[1]"
            />

            <div className="flex flex-col items-center justify-center pt-24 pb-4 lg:pt-0 lg:pb-0 px-6 relative z-10">
              <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center lg:-mt-32">

                {/* Text Column: Copy & Input (First on Mobile) */}
                <div className="lg:col-span-7 space-y-8 max-w-3xl flex flex-col items-center text-center lg:items-start lg:text-left order-1">
                  <div className="space-y-5">
                    <motion.h1
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-3xl sm:text-4xl md:text-5xl lg:text-[2.6rem] xl:text-[2.95rem] font-bold tracking-tight leading-[1.12]"
                    >
                      {lang === 'en' ? (
                        <>
                          <span className="text-white block lg:whitespace-nowrap">Your End-to-End Autonomous</span>
                          <span className="bg-gradient-to-r from-white via-[#dcd8fc] to-[#a89ef5] bg-clip-text text-transparent block lg:whitespace-nowrap">
                            Spatial AI Platform.
                          </span>
                        </>
                      ) : (
                        <>
                          <span className="text-white block lg:whitespace-nowrap">منصتك المكانية الشاملة والمستقلة</span>
                          <span className="bg-gradient-to-r from-white via-[#dcd8fc] to-[#a89ef5] bg-clip-text text-transparent block lg:whitespace-nowrap">
                            للذكاء الاصطناعي الجغرافي.
                          </span>
                        </>
                      )}
                    </motion.h1>

                    <motion.p
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-slate-300/90 text-base md:text-lg font-normal max-w-xl leading-relaxed"
                    >
                      {t.subtitle}
                    </motion.p>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                    className="space-y-5 w-full flex flex-col items-center lg:items-start"
                  >
                    {/* Interactive Chat Input */}
                    <div className="relative group w-full max-w-xl">
                      <div className="absolute -inset-1 bg-gradient-to-r from-[#7F77DD]/30 to-purple-500/20 rounded-2xl blur opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200" />
                      <div className="relative flex items-center bg-[#0a0c14] border border-white/10 rounded-2xl p-2 pl-6 shadow-2xl">
                        <input
                          type="text"
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              handleStartNow();
                            }
                          }}
                          placeholder={t.placeholder}
                          className="bg-transparent border-none outline-none text-white flex-grow text-[15px] placeholder:text-slate-500 font-medium"
                        />
                        <button
                          onClick={handleStartNow}
                          className="w-12 h-12 rounded-full bg-[#7F77DD] flex items-center justify-center text-white hover:bg-[#938be8] transition-all shadow-lg hover:scale-105 active:scale-95 group/btn"
                        >
                          <ArrowRight className={`${isRtl ? 'rotate-180' : ''} group-hover/btn:translate-x-0.5 transition-transform`} size={20} />
                        </button>
                      </div>
                    </div>

                    {/* Trust indicator - Minimalist & Pure */}
                    <div className="flex items-center gap-2.5 text-[11px] font-semibold text-slate-400 uppercase tracking-[0.16em] pl-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#7F77DD] shadow-[0_0_8px_#7F77DD] shrink-0" />
                      <span>{t.trust}</span>
                    </div>
                  </motion.div>
                </div>

                {/* Visual Column: Logo Signature (Second on Mobile) */}
                <div className="lg:col-span-5 relative order-2 flex justify-center lg:justify-end">
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    className="relative w-[280px] h-[280px] md:w-[700px] md:h-[700px] lg:w-[900px] lg:h-[900px]"
                  >
                    {/* Heavy Background Glows */}
                    <div className="absolute inset-[-8%] bg-[#7F77DD]/24 blur-[120px] rounded-full animate-pulse" />
                    <div className="absolute inset-[-6%] bg-[#b19cf4]/14 blur-[120px] rounded-full translate-x-10 translate-y-10" />

                    <div className="relative z-10 h-full w-full">
                      <NovaHeroLogoStage />
                    </div>
                  </motion.div>
                </div>

              </div>
            </div>

            {/* Marquee Teaser - Restore 3 Rows */}
            <div className="pb-8 pt-2 lg:-mt-44 overflow-hidden relative z-10 border-t border-white/[0.03] bg-white/[0.01]">
              <div className="flex flex-col gap-5 py-6">
                <style dangerouslySetInnerHTML={{
                  __html: sanitizeCss(`
                @keyframes marquee-left { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
                @keyframes marquee-right { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
                .animate-marquee-left { animation: marquee-left 70s linear infinite; }
                .animate-marquee-right { animation: marquee-right 70s linear infinite; }
                .pause-on-hover:hover { animation-play-state: paused; }
              `)
                }} />

                <div className="flex overflow-hidden">
                  <div className="flex gap-4 whitespace-nowrap animate-marquee-left pause-on-hover">
                    {row1.map((prompt, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setMessage(prompt);
                          setIsPreparing(true);
                          setTimeout(() => {
                            navigate(`/nova-workspace?q=${encodeURIComponent(prompt)}`);
                          }, 1000); // Shorter delay for direct clicks
                        }}
                        className="px-5 py-3.5 rounded-2xl bg-white/[0.02] border border-white/5 text-[14px] text-slate-400 font-medium hover:bg-white/10 hover:border-[#7F77DD]/30 hover:text-[#9f96f3] transition-all cursor-pointer"
                      >
                        {prompt}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex overflow-hidden">
                  <div className="flex gap-4 whitespace-nowrap animate-marquee-right pause-on-hover">
                    {row2.map((prompt, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setMessage(prompt);
                          setIsPreparing(true);
                          setTimeout(() => {
                            navigate(`/nova-workspace?q=${encodeURIComponent(prompt)}`);
                          }, 1000); // Shorter delay for direct clicks
                        }}
                        className="px-5 py-3.5 rounded-2xl bg-white/[0.02] border border-white/5 text-[14px] text-slate-400 font-medium hover:bg-white/10 hover:border-[#7F77DD]/30 hover:text-[#9f96f3] transition-all cursor-pointer"
                      >
                        {prompt}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex overflow-hidden">
                  <div className="flex gap-4 whitespace-nowrap animate-marquee-left pause-on-hover">
                    {row3.map((prompt, i) => (
                      <div
                        key={i}
                        onClick={() => {
                          setMessage(prompt);
                          setIsPreparing(true);
                          setTimeout(() => {
                            navigate(`/nova-workspace?q=${encodeURIComponent(prompt)}`);
                          }, 1000); // Shorter delay for direct clicks
                        }}
                        className="px-5 py-3.5 rounded-2xl bg-white/[0.02] border border-white/5 text-[14px] text-slate-400 font-medium hover:bg-white/10 hover:border-[#7F77DD]/30 hover:text-[#9f96f3] transition-all cursor-pointer"
                      >
                        {prompt}
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            </div>
          </section>

          {/* 2. Success Stories Section - Moved up */}
          <section id="features" className="pt-0 pb-24 px-6 overflow-hidden">
            <div className="max-w-6xl mx-auto">

              <div className="space-y-32">
                {t.useCases.items.map((item, i) => (
                  <div key={i} className="flex flex-col items-center gap-16">
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-50px" }}
                      transition={{ duration: 0.6, ease: "easeOut" }}
                      className="w-full max-w-3xl text-center space-y-6"
                    >
                      <h3 className="text-2xl md:text-4xl font-bold tracking-tight leading-tight">{item.title}</h3>
                      <p className="text-slate-400 text-base md:text-lg font-medium leading-relaxed max-w-2xl mx-auto">{item.sub}</p>
                      <div className="pt-4 flex justify-center">
                        <button className="flex items-center gap-2 text-[#7F77DD] font-bold uppercase text-[11px] tracking-widest group">
                          {isRtl ? 'استكشف المزيد' : 'Learn more'}
                          <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                        </button>
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 50 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-100px" }}
                      transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
                      className="w-full relative"
                    >
                      <div className="absolute inset-x-[-15%] inset-y-[-15%] bg-[#7F77DD]/5 blur-[100px] rounded-full opacity-20 pointer-events-none" />
                      <VisualProofImage alt={item.alt} src={item.src} />
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* 3. Psychological Struggle Section - Standard Headers */}
          <section className="py-48 bg-[#050608]/50 relative overflow-hidden border-y border-white/[0.02]">
            <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-[0.02] pointer-events-none" />
            <div className="max-w-5xl mx-auto px-6 relative z-10">
              <div className="text-center mb-24">
                <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-white mb-6 underline underline-offset-8 decoration-red-500/30 decoration-2">{t.struggle.title}</h2>
                <p className="text-slate-500 text-lg font-medium">{t.struggle.subtitle}</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {t.struggle.items.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-50px" }}
                    transition={{ delay: i * 0.1, duration: 0.6, ease: "easeOut" }}
                    className="p-8 rounded-2xl bg-white/[0.02] border border-white/5 flex items-center gap-6 group hover:border-red-500/10 transition-all"
                  >
                    <div className="w-11 h-11 rounded-2xl bg-red-500/5 flex items-center justify-center group-hover:bg-red-500/10 transition-all">
                      {React.cloneElement(item.icon as any, { size: 20 })}
                    </div>
                    <span className="text-lg font-bold text-slate-300">{item.label}</span>
                  </motion.div>
                ))}
              </div>

              <div className="mt-28 text-center">
                <motion.div
                  initial={{ scale: 0.95, opacity: 0, y: 30 }}
                  whileInView={{ scale: 1, opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ duration: 0.8, ease: "easeOut" }}
                  className="inline-flex flex-col items-center gap-8"
                >
                  <div className="w-px h-16 bg-gradient-to-b from-transparent via-[#7F77DD]/50 to-transparent" />
                  <h3 className="text-4xl md:text-6xl font-bold tracking-tight text-[#7F77DD] mb-4 animate-pulse">
                    {t.struggle.solution}
                  </h3>
                </motion.div>
              </div>
            </div>
          </section>

          {/* 4. Live Demo Mockup */}
          <section className="py-48 px-6">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-5xl font-bold text-center mb-16 tracking-tight">{t.demo.title}</h2>
              <div className="rounded-2xl bg-white/[0.02] border border-white/10 p-5 md:p-10 backdrop-blur-sm relative group overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#7F77DD]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />

                <div className="flex justify-end mb-8">
                  <div className="max-w-[80%] px-6 py-4 rounded-2xl bg-white/5 border border-white/10 text-slate-200 text-[15px]">
                    {t.demo.userQuery}
                  </div>
                </div>

                <div className="flex justify-start">
                  <div className="max-w-[90%] px-6 py-8 rounded-2xl bg-[#7F77DD]/5 border border-[#7F77DD]/10">
                    <div className="flex items-center gap-3 mb-6 text-[#7F77DD] font-bold uppercase text-[10px] tracking-widest">
                      <Cpu size={14} /> VEXL Nova AI
                    </div>
                    <div className="bg-black/50 p-6 rounded-2xl border border-white/5 mb-6 overflow-x-auto">
                      <pre className="text-[#9f96f3]/70 font-mono text-[13px] leading-relaxed">
                        {t.demo.response.code}
                      </pre>
                    </div>
                    <p className="text-slate-300 text-[16px] leading-relaxed">
                      {t.demo.response.explanation}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-24 px-6 border-t border-white/5">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6 italic uppercase tracking-widest">{isRtl ? 'الاستثمار الأذكى في مستقبلك' : 'The Smartest Investment in Your Future'}</h2>
              <p className="text-slate-400 mb-8 max-w-xl mx-auto">{isRtl ? 'خطط مرنة تبدأ من المجاني للمجتمع، مصممة بدقة لتناسب المهندسين والباحثين والمؤسسات.' : 'Flexible licensing tiers starting from Community Free, engineered for spatial engineers, researchers, and enterprises.'}</p>
              <button
                onClick={() => navigate('/nova/pricing')}
                className="px-8 py-3 rounded-full bg-[#7F77DD] text-white font-black uppercase text-[10px] tracking-widest hover:bg-[#938be8] transition-all shadow-[0_0_20px_rgba(127,119,221,0.3)]"
              >
                {isRtl ? 'مشاهدة تفاصيل الأسعار' : 'View Detailed Pricing'}
              </button>
            </div>
          </section>

          {/* 6. Final Positioning - No Italics */}
          <section className="py-64 relative overflow-hidden text-center bg-[#02040a]">
            <div className="absolute inset-0 bg-gradient-to-t from-[#7F77DD]/10 to-transparent opacity-20" />
            <div className="max-w-4xl mx-auto px-6 relative z-10">
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="mb-12"
              >
                <div className="w-20 h-20 rounded-2xl bg-[#7F77DD]/10 flex items-center justify-center text-[#7F77DD] mx-auto mb-12 border border-[#7F77DD]/20">
                  <UserCheck size={32} />
                </div>
                <h2 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight text-white mb-10">
                  {isRtl ? 'محرك الاستدلال المكاني للجيوماتكس و GIS — جاهز دائماً' : 'Your sovereign Spatial AI Reasoning Engine — always online'}
                </h2>
                <p className="text-slate-400 text-lg font-medium mb-16 max-w-2xl mx-auto leading-relaxed">
                  {t.finalCTA}
                </p>
                <div className="flex flex-col md:flex-row justify-center gap-6">
                  <button
                    onClick={handleStartNow}
                    className="px-12 py-5 rounded-full bg-[#7F77DD] text-white font-bold uppercase tracking-widest text-[12px] shadow-lg hover:scale-[1.03] transition-all"
                  >
                    {lang === 'en' ? 'Create Free Account' : 'أنشئ حسابك المجاني'}
                  </button>
                </div>
              </motion.div>
            </div>

            {/* FAQ SECTION (GEO & Traditional SEO Citable Content) */}
            <div className="max-w-4xl mx-auto px-6 pb-20 pt-20 border-t border-white/5 text-left">
              <h3 className="text-3xl font-bold tracking-tight text-white mb-4 text-center">
                {t.faq.title}
              </h3>
              <p className="text-slate-400 text-sm max-w-xl mx-auto mb-12 text-center">
                {t.faq.subtitle}
              </p>

              <div className="space-y-6">
                {t.faq.items.map((item, idx) => (
                  <div key={idx} className="p-6 rounded-2xl border border-white/5 bg-[#050E1C]/20 backdrop-blur-md hover:border-[#7F77DD]/20 transition-all duration-300">
                    <h4 className="text-base font-bold text-white mb-2 flex gap-2">
                      <span className="text-[#7F77DD]">؟</span>
                      {item.q}
                    </h4>
                    <p className="text-xs md:text-sm text-slate-400 leading-relaxed font-medium">
                      {item.a}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* AI-Ready Structured Definition Section */}
            <div className="max-w-4xl mx-auto px-6 pb-32 border-t border-white/5 pt-20">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                <div>
                  <h4 className="text-lg font-bold text-[#7F77DD] mb-6 uppercase tracking-widest">
                    {isRtl ? 'ما هو VEXL Nova للطلاب؟' : 'What is VEXL Nova for Students?'}
                  </h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {isRtl
                      ? 'VEXL Nova هو وكيل ذكاء اصطناعي (Agentic AI) مصمم ليكون المساعد الشخصي لطلاب GIS. يساعدهم في فهم المفاهيم المكانية المعقدة، وأتمتة المهام البرمجية في ArcGIS Pro، وبناء مشاريع التخرج بذكاء.'
                      : 'VEXL Nova is an agentic AI assistant architected to be the personal mentor for GIS students. It helps them master complex spatial concepts, automate ArcGIS Pro coding tasks, and engineer graduation projects with intelligence.'
                    }
                  </p>
                </div>
                <div>
                  <h4 className="text-lg font-bold text-[#7F77DD] mb-6 uppercase tracking-widest">
                    {isRtl ? 'التركيز التقني (Entity Context)' : 'Technical Focus (Entity Context)'}
                  </h4>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {isRtl
                      ? 'نحن نركز بشكل أساسي على أتمتة الـ GIS (GIS Automation)، الـ GeoAI، واستخدام لغة بايثون في التحليل المكاني، مما يجعله الأداة الأدق لطلاب الهندسة ونظم المعلومات الجغرافية.'
                      : 'We focus primarily on GIS Automation, GeoAI, and the use of Python (ArcPy/PyGEE) in spatial analysis, making it the most precise tool for engineering and GIS students worldwide.'
                    }
                  </p>
                </div>
              </div>
            </div>
          </section>
        </div>
      </PageTransition>
      <footer className="py-20 bg-black/50 border-t border-white/5 text-center">
        <p className="text-slate-600 text-xs font-mono tracking-widest uppercase">© 2026 VEXL STUDIO • NOVA TERMINAL • {lang === 'ar' ? 'القاهرة' : 'CAIRO'}</p>
      </footer>
    </>
  );
};

export default NovaPage;
